import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const W = 1080;
const H = 1080;

function GraphicPreviewMinimal({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const { homeTeam, awayTeam, gameDate, gameTime, location } = graphicData;

  const isLightColor = (color) => {
    if (!color || color === 'null') return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
  };

  const getBackground = () => {
    const p = teamColors.primary;
    const s = teamColors.secondary;
    switch (backgroundStyle) {
      case 'radial-gradient': return `radial-gradient(circle at center, ${p} 0%, ${s} 100%)`;
      case 'diagonal-gradient': return `linear-gradient(45deg, ${p} 0%, ${s} 100%)`;
      case 'split-two-tone': return `linear-gradient(90deg, ${p} 0%, ${p} 50%, ${s} 50%, ${s} 100%)`;
      case 'solid-primary': return p;
      case 'solid-secondary': return s;
      default: return `linear-gradient(135deg, ${p} 0%, ${s} 100%)`;
    }
  };

  const primaryLight = isLightColor(teamColors.primary);
  const secondaryLight = isLightColor(teamColors.secondary);
  const textColor = (primaryLight && secondaryLight) || primaryLight ? '#2d3748' : '#ffffff';
  const textShadow = textColor === '#ffffff'
    ? '0 2px 8px rgba(0,0,0,0.4)'
    : '0 1px 3px rgba(255,255,255,0.3)';

  const detailColor = detailTextColor || textColor;
  const gameDayFontSize = Math.max(28, Math.round(heroFontSize / 2));
  const homeNameFontSize = (homeTeam || 'YOUR TEAM').length > 14 ? 100 : (homeTeam || 'YOUR TEAM').length > 9 ? 124 : 152;
  const awayNameFontSize = (awayTeam || 'OPPONENT').length > 14 ? 100 : (awayTeam || 'OPPONENT').length > 9 ? 124 : 152;

  const logoCircleStyle = {
    width: 140,
    height: 140,
    borderRadius: '50%',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  const detailBoxBg = textColor === '#ffffff'
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(0,0,0,0.08)';
  const detailBoxBorder = textColor === '#ffffff'
    ? '2px solid rgba(255,255,255,0.2)'
    : '2px solid rgba(0,0,0,0.1)';

  return (
    <div
      id="graphic-preview"
      style={{
        width: W,
        height: H,
        background: getBackground(),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        border: '2px solid rgba(255,255,255,0.15)',
        borderRadius: 16,
        fontFamily: 'Barlow, sans-serif',
      }}
    >
      {/* Home Logo — top left */}
      <div style={{ position: 'absolute', top: 60, left: 60, zIndex: 10 }}>
        <div style={logoCircleStyle}>
          {homeTeamLogo ? (
            <img
              src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
              alt={homeTeam}
              style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
            />
          ) : (
            <span style={{ fontSize: 72 }}>
              {teamSport === 'Softball' ? '🥎' : '⚾'}
            </span>
          )}
        </div>
      </div>

      {/* Opponent Logo — top right */}
      <div style={{ position: 'absolute', top: 60, right: 60, zIndex: 10 }}>
        <div style={logoCircleStyle}>
          {awayTeamLogoPreview ? (
            <img
              src={awayTeamLogoPreview}
              alt={awayTeam}
              style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
            />
          ) : (
            <span style={{ fontSize: 72 }}>
              {teamSport === 'Softball' ? '🥎' : '⚾'}
            </span>
          )}
        </div>
      </div>

      {/* Center Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '0 80px',
          zIndex: 1,
        }}
      >
        {/* GAME DAY */}
        <div
          style={{
            fontFamily: heroFont,
            fontSize: gameDayFontSize,
            fontWeight: 700,
            color: textColor,
            letterSpacing: 8,
            opacity: 0.9,
            marginBottom: 48,
          }}
        >
          GAME DAY
        </div>

        {/* Team Names */}
        <div style={{ marginBottom: 64 }}>
          <div
            style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: homeNameFontSize,
              fontWeight: 900,
              color: textColor,
              textShadow,
              lineHeight: 1,
              marginBottom: 16,
            }}
          >
            {homeTeam || 'YOUR TEAM'}
          </div>
          <div
            style={{
              fontFamily: 'Barlow, sans-serif',
              fontSize: 48,
              fontWeight: 300,
              color: textColor,
              opacity: 0.7,
              letterSpacing: 12,
              margin: '24px 0',
            }}
          >
            VS
          </div>
          <div
            style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: awayNameFontSize,
              fontWeight: 900,
              color: textColor,
              textShadow,
              lineHeight: 1,
            }}
          >
            {awayTeam || 'OPPONENT'}
          </div>
        </div>

        {/* Details Box */}
        {(gameDate || gameTime || location) && (
          <div
            style={{
              background: detailBoxBg,
              border: detailBoxBorder,
              borderRadius: 24,
              padding: '40px 64px',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              minWidth: 500,
            }}
          >
            {gameDate && (
              <div
                style={{
                  fontFamily: 'Barlow, sans-serif',
                  fontSize: 36,
                  fontWeight: 600,
                  color: detailColor,
                  letterSpacing: 1,
                  textAlign: 'center',
                }}
              >
                {formatGameDate(gameDate, dateFormat)}
              </div>
            )}
            {(gameTime || location) && (
              <div
                style={{
                  fontFamily: 'Barlow, sans-serif',
                  fontSize: 30,
                  fontWeight: 500,
                  color: detailColor,
                  opacity: 0.85,
                  letterSpacing: 0.5,
                  textAlign: 'center',
                }}
              >
                {gameTime && location
                  ? `${formatGameTime(gameTime)} · ${location}`
                  : gameTime
                    ? formatGameTime(gameTime)
                    : location}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <img
            src="/PMlogoleft.png"
            alt="Playmaker Media Kit"
            style={{ height: 80, width: 'auto', display: 'block' }}
          />
        </div>
      )}
    </div>
  );
}

export default GraphicPreviewMinimal;
